import React, { useState, useRef } from "react";
import { Upload, FileText, CheckCircle, ShieldCheck, CreditCard, X, Loader2, Camera } from "lucide-react";
import { IPWebcamCapture } from "./IPWebcamCapture";

export function HealthSchemeRegistration() {
  const [patientId, setPatientId] = useState("");
  const [schemeName, setSchemeName] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [govIdType, setGovIdType] = useState("Aadhaar");
  const [govIdNumber, setGovIdNumber] = useState("");

  const [healthCardImage, setHealthCardImage] = useState<string | null>(null);
  const [govIdImage, setGovIdImage] = useState<string | null>(null);
  
  const [healthCaptureMode, setHealthCaptureMode] = useState<"file" | "webcam">("file");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const healthInputRef = useRef<HTMLInputElement>(null);
  const govInputRef = useRef<HTMLInputElement>(null);

  const handleImageUpload = (file: File, type: "health" | "gov") => {
    if (file && file.type.startsWith("image/")) {
      const url = URL.createObjectURL(file);
      if (type === "health") setHealthCardImage(url);
      else setGovIdImage(url);
    }
  };

  const handleDrop = (e: React.DragEvent, type: "health" | "gov") => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleImageUpload(e.dataTransfer.files[0], type);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Mock network request
    await new Promise(r => setTimeout(r, 1500));
    setIsSubmitting(false);
    setSuccess(true);
    
    // Reset form after success message
    setTimeout(() => {
      setSuccess(false);
      setPatientId("");
      setSchemeName("");
      setCardNumber("");
      setGovIdType("Aadhaar");
      setGovIdNumber("");
      if (healthCardImage) URL.revokeObjectURL(healthCardImage);
      if (govIdImage) URL.revokeObjectURL(govIdImage);
      setHealthCardImage(null);
      setGovIdImage(null);
    }, 3000);
  };

  const clearImage = (type: "health" | "gov", e: React.MouseEvent) => {
    e.stopPropagation();
    if (type === "health") {
      if (healthCardImage) URL.revokeObjectURL(healthCardImage);
      setHealthCardImage(null);
    } else {
      if (govIdImage) URL.revokeObjectURL(govIdImage);
      setGovIdImage(null);
    }
  };

  return (
    <div className="bg-[#040d1a]/80 backdrop-blur-xl border border-teal-500/20 rounded-3xl p-6 relative overflow-hidden animate-fade-in mt-6">
      <div className="absolute top-0 left-0 w-1/2 h-1 bg-gradient-to-r from-teal-500 to-transparent" />
      
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-teal-500/20 flex items-center justify-center border border-teal-500/30">
          <ShieldCheck className="w-5 h-5 text-teal-400" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-white">Health Scheme Registration</h3>
          <p className="text-gray-400 text-xs">Verify government IDs and upload health scheme cards.</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Patient Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Patient ID *</label>
            <input 
              required
              type="text" 
              placeholder="e.g. P-10042" 
              value={patientId}
              onChange={e => setPatientId(e.target.value)}
              className="w-full bg-[#071428] border border-white/10 text-white text-sm rounded-xl px-4 py-3 focus:outline-none focus:border-teal-500/50 focus:ring-1 focus:ring-teal-500/50 transition-all" 
            />
          </div>
        </div>

        <hr className="border-white/5" />

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
          
          {/* Health Card Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-teal-400 flex items-center gap-2">
                <CreditCard className="w-4 h-4" /> Health Card Details
              </h4>
              {!healthCardImage && (
                <button
                  type="button"
                  onClick={() => setHealthCaptureMode(m => m === "file" ? "webcam" : "file")}
                  className="flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-medium rounded-md bg-white/5 border border-white/10 hover:bg-white/10 text-gray-300 transition-colors"
                >
                  <Camera className="w-3 h-3" />
                  {healthCaptureMode === "file" ? "IP Webcam" : "File Upload"}
                </button>
              )}
            </div>
            
            <div className="space-y-3">
              <div>
                <label className="block text-[11px] text-gray-500 uppercase tracking-wide mb-1">Scheme Name</label>
                <select 
                  value={schemeName}
                  onChange={e => setSchemeName(e.target.value)}
                  className="w-full bg-[#071428] border border-white/10 text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-teal-500"
                >
                  <option value="">Select Scheme</option>
                  <option value="Ayushman Bharat (PMJAY)">Ayushman Bharat (PMJAY)</option>
                  <option value="CGHS">CGHS</option>
                  <option value="ECHS">ECHS</option>
                  <option value="State Health Insurance">State Health Insurance</option>
                </select>
              </div>
              
              <div>
                <label className="block text-[11px] text-gray-500 uppercase tracking-wide mb-1">Card Number</label>
                <input 
                  type="text" 
                  value={cardNumber}
                  onChange={e => setCardNumber(e.target.value)}
                  placeholder="Enter Card Number" 
                  className="w-full bg-[#071428] border border-white/10 text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-teal-500" 
                />
              </div>
            </div>

            {/* Health Card Upload */}
            {healthCaptureMode === "webcam" && !healthCardImage ? (
              <div className="mt-4">
                <IPWebcamCapture 
                  onCapture={(url) => {
                    setHealthCardImage(url);
                    setHealthCaptureMode("file"); // switch back to file mode for display
                  }} 
                  onCancel={() => setHealthCaptureMode("file")}
                />
              </div>
            ) : (
              <div 
                className={`relative mt-4 h-32 rounded-xl border-2 border-dashed flex flex-col items-center justify-center cursor-pointer overflow-hidden transition-colors ${healthCardImage ? 'border-teal-500/50' : 'border-white/10 hover:border-teal-500/30 bg-white/5 hover:bg-white/10'}`}
                onDragOver={e => e.preventDefault()}
                onDrop={e => handleDrop(e, "health")}
                onClick={() => !healthCardImage && healthInputRef.current?.click()}
              >
                <input ref={healthInputRef} type="file" accept="image/*" className="hidden" onChange={e => e.target.files && handleImageUpload(e.target.files[0], "health")} />
                
                {healthCardImage ? (
                  <>
                    <img src={healthCardImage} alt="Health Card" className="absolute inset-0 w-full h-full object-cover opacity-80" />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                      <button type="button" onClick={(e) => clearImage("health", e)} className="p-2 bg-red-500/80 rounded-full text-white hover:bg-red-500">
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <Upload className="w-6 h-6 text-gray-500 mb-2" />
                    <span className="text-xs text-gray-400">Click or drag Health Card photo</span>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Gov ID Section */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-blue-400 flex items-center gap-2">
              <FileText className="w-4 h-4" /> Government ID Proof
            </h4>
            
            <div className="space-y-3">
              <div>
                <label className="block text-[11px] text-gray-500 uppercase tracking-wide mb-1">ID Type</label>
                <select 
                  value={govIdType}
                  onChange={e => setGovIdType(e.target.value)}
                  className="w-full bg-[#071428] border border-white/10 text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-blue-500"
                >
                  <option value="Aadhaar">Aadhaar Card</option>
                  <option value="PAN">PAN Card</option>
                  <option value="Voter ID">Voter ID</option>
                  <option value="Passport">Passport</option>
                  <option value="Driving License">Driving License</option>
                </select>
              </div>
              
              <div>
                <label className="block text-[11px] text-gray-500 uppercase tracking-wide mb-1">ID Number</label>
                <input 
                  type="text" 
                  value={govIdNumber}
                  onChange={e => setGovIdNumber(e.target.value)}
                  placeholder={`Enter ${govIdType} Number`} 
                  className="w-full bg-[#071428] border border-white/10 text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-blue-500" 
                />
              </div>
            </div>

            {/* Gov ID Upload */}
            <div 
              className={`relative mt-4 h-32 rounded-xl border-2 border-dashed flex flex-col items-center justify-center cursor-pointer overflow-hidden transition-colors ${govIdImage ? 'border-blue-500/50' : 'border-white/10 hover:border-blue-500/30 bg-white/5 hover:bg-white/10'}`}
              onDragOver={e => e.preventDefault()}
              onDrop={e => handleDrop(e, "gov")}
              onClick={() => !govIdImage && govInputRef.current?.click()}
            >
              <input ref={govInputRef} type="file" accept="image/*" className="hidden" onChange={e => e.target.files && handleImageUpload(e.target.files[0], "gov")} />
              
              {govIdImage ? (
                <>
                  <img src={govIdImage} alt="Government ID" className="absolute inset-0 w-full h-full object-cover opacity-80" />
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                    <button type="button" onClick={(e) => clearImage("gov", e)} className="p-2 bg-red-500/80 rounded-full text-white hover:bg-red-500">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <Upload className="w-6 h-6 text-gray-500 mb-2" />
                  <span className="text-xs text-gray-400">Click or drag {govIdType} photo</span>
                </>
              )}
            </div>
          </div>

        </div>

        {/* Submit Area */}
        <div className="pt-6 border-t border-white/5 flex items-center justify-between">
          {success ? (
            <div className="flex items-center gap-2 text-green-400 animate-slide-in">
              <CheckCircle className="w-5 h-5" />
              <span className="text-sm font-medium">Patient successfully registered!</span>
            </div>
          ) : (
            <p className="text-xs text-gray-500">Ensure images are clear and readable.</p>
          )}
          
          <button 
            type="submit" 
            disabled={!patientId || !schemeName || !cardNumber || !govIdNumber || isSubmitting || success}
            className="flex items-center gap-2 px-6 py-2.5 bg-teal-500 hover:bg-teal-400 disabled:opacity-50 disabled:hover:bg-teal-500 text-white font-bold rounded-xl text-sm transition-all shadow-[0_0_15px_rgba(20,184,166,0.3)] disabled:shadow-none"
          >
            {isSubmitting ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Registering...</>
            ) : (
              "Complete Registration"
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
